import { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../../../utils/axios';
import Modal, { BotaoSecundario } from '../../../../components/gps/Modal';
import { Num } from '../../../../components/gps';

/* ============================================================================
   HistoricoGeral - o feed de eventos da ultima hora, na Home.

   Migrado do MUI (Card/Chip/Grid/Dialog/Table) para o visual novo. A busca, a
   janela de uma hora, o auto-refresh de 60s e o relogio de "ha Ns" ficaram
   iguais - e informacao operacional: quem olha a Home quer saber se o que esta
   vendo e de agora.

   POR QUE AS CORES DE CATEGORIA CONTINUAM EM HEX, e nao em token:
   os sete tipos (Dispositivo, Entidade, Politica, Usuario, Aplicativo, Regional,
   APK) precisam ser DISTINGUIVEIS entre si; nao existe token semantico para
   "quarta categoria", e inventar um seria mentir sobre o que a cor significa.
   Vao por `style` inline de proposito: classe Tailwind com valor arbitrario e
   opacidade (`bg-[rgb(21_101_192_/_0.04)]`) NAO gera CSS nenhum - o Tailwind le a
   barra como modificador e a regra desaparece sem aviso no build. `style` nao tem
   essa armadilha.

   TRES CORRECOES:

   1. "Dados alterados" mostrava JSON cru. O `dataCampo` do sistema e um ARRAY de
      `{campo, antigo, novo}`, e o codigo antigo fazia `Object.entries` nele -
      entao cada linha saia como `0` → `{"campo":"Nome","antigo":...}`. Agora o
      array e renderizado como tabela antes/depois, igual ao resto do sistema, e
      objeto continua caindo no formato chave/valor.

   2. O botao de detalhes aparecia SEMPRE, mesmo sem `chaveInfo` nem `dataCampo`
      (so com opacidade menor): clicar abria um modal com "nenhum dado
      adicional". Agora ele nao existe quando nao ha o que abrir.

   3. `formatCompactNumber` dizia "1,2 mil na ultima hora" sem o total exato. O
      numero cheio fica no title.

   A funcao `fixEncoding` fica onde estava, com o comentario original: e remendo
   de dado gravado errado (VARCHAR sem Unicode), nao de exibicao. A correcao de
   verdade e na coluna do banco - esta registrada no PENDENCIAS.
   ============================================================================ */

const TIPO_LABELS: Record<number, { label: string; color: string }> = {
  0: { label: 'Dispositivo', color: '#1565c0' },
  1: { label: 'Entidade', color: '#6a1b9a' },
  2: { label: 'Política', color: '#e65100' },
  3: { label: 'Usuário', color: '#2e7d32' },
  4: { label: 'Aplicativo', color: '#00838f' },
  5: { label: 'Regional', color: '#4527a0' },
  6: { label: 'APK', color: '#558b2f' },
};

const ACAO_LABELS: Record<number, string> = {
  0: 'Login',
  1: 'Criar',
  2: 'Editar',
  3: 'Deletar',
  4: 'Aprovar',
  5: 'Bloquear',
  6: 'Negar',
  7: 'Reset',
  8: 'Reiniciado',
  9: 'Sincronizar',
  10: 'Apagou',
  11: 'CR',
  12: 'Sincronização GPS',
  13: 'Resetou Senha',
  14: 'Perda/Roubo',
  15: 'Tocar Som',
  16: 'Limpar Cache',
  17: 'Recadastrar',
  18: 'Trocou Senha',
  19: 'Verificação Campos',
};

interface UsuarioExecutante {
  codigo?: number;
  nome?: string;
  email?: string;
}

interface ChaveInfo {
  tipo: 'usuario' | 'dispositivo' | 'politica' | 'entidade' | 'regional';
  id?: number | string;
  nome?: string;
  email?: string;
  deviceId?: string;
  imei?: string;
  name?: string;
}

interface HistoricoItem {
  codigo: number;
  mensagem: string;
  dataExecucao: string;
  ip: string;
  tipo: number;
  acao: number;
  chave: string;
  dataCampo?: string | null;
  usuarioExecutante?: UsuarioExecutante | null;
  chaveInfo?: ChaveInfo | null;
}

interface PaginadoResponse {
  content: HistoricoItem[];
  totalElements: number;
}

interface ModalState {
  open: boolean;
  chaveInfo?: ChaveInfo | null;
  dataCampo?: string | null;
  titulo?: string;
}

/**
 * Corrige padrões de encoding quebrado que chegam como ?? no banco.
 * Ocorre quando caracteres Unicode (ç, ã, õ) foram inseridos em colunas VARCHAR
 * sem suporte a Unicode — cada caractere multi-byte virou '?'.
 */
function fixEncoding(text: string | null | undefined): string {
  if (!text) return text ?? '';
  return text
    .replace(/\?\?o/g, 'ção')
    .replace(/\?\?es/g, 'ções')
    .replace(/\?\?a/g, 'çã')
    .replace(/([nmpbl])\?o\b/g, '$1ão')
    .replace(/\?\?/g, 'çã');
}

function formatDateTime(value: string) {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

function formatCompactNumber(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    notation: value >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function parseJsonSafe(raw: string | null | undefined): any {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return parsed;
  } catch {}
  return null;
}

function chaveInfoToRows(info: ChaveInfo): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  if (info.tipo === 'usuario') {
    if (info.id != null) rows.push({ label: 'ID', value: String(info.id) });
    if (info.nome) rows.push({ label: 'Nome', value: info.nome });
    if (info.email) rows.push({ label: 'E-mail', value: info.email });
  } else if (info.tipo === 'dispositivo') {
    if (info.deviceId) rows.push({ label: 'Device ID', value: info.deviceId });
    if (info.nome) rows.push({ label: 'Nome', value: info.nome });
    if (info.imei) rows.push({ label: 'IMEI / Serial', value: info.imei });
  } else if (info.tipo === 'politica') {
    if (info.name) rows.push({ label: 'Identificador', value: info.name });
    if (info.nome) rows.push({ label: 'Nome', value: info.nome });
  } else if (info.tipo === 'entidade') {
    if (info.id != null) rows.push({ label: 'ID', value: String(info.id) });
    if (info.nome) rows.push({ label: 'Nome', value: info.nome });
  } else if (info.tipo === 'regional') {
    if (info.id != null) rows.push({ label: 'ID', value: String(info.id) });
    if (info.nome) rows.push({ label: 'Nome', value: info.nome });
  }
  return rows;
}

/* ABAS por GRUPO DE ACAO.

   POR QUE ESTES QUATRO: a pergunta que traz alguem ao feed nunca e "o que
   aconteceu" em geral - e "o que alguem mudou", "o que foi apagado" ou "quem
   entrou". Sem separacao, as tres perguntas dividem a mesma lista de 79 eventos por
   hora e nenhuma delas e respondida.

   `Alteracoes` e o grupo RESIDUAL de proposito: tudo que nao e criar, apagar ou
   login cai nele (editar, aprovar, negar, bloquear, reset de senha, reinicio,
   sincronizacao...). Assim nenhum evento fica invisivel em todas as abas - o que
   seria pior que nao ter aba, porque o feed passaria a esconder coisa sem avisar.

   O filtro e LOCAL, sobre o que ja veio na resposta: o endpoint de historicos nao
   tem parametro de acao, e a janela e de uma hora - nao vale uma consulta por aba.
   Por isso `PAGE_SIZE` subiu de 12 para 60: com filtro local, 12 eventos deixariam
   quase toda aba vazia. */
const ACOES_CRIACAO = [1];
const ACOES_REMOCAO = [3, 10];
const ACOES_ACESSO = [0];

const ABAS = [
  { id: 'tudo', rotulo: 'Tudo' },
  { id: 'alteracoes', rotulo: 'Alterações' },
  { id: 'criacoes', rotulo: 'Criações' },
  { id: 'remocoes', rotulo: 'Remoções' },
  { id: 'acessos', rotulo: 'Acessos' },
] as const;

type AbaId = (typeof ABAS)[number]['id'];

function grupoDaAcao(acao: number): Exclude<AbaId, 'tudo'> {
  if (ACOES_CRIACAO.includes(acao)) return 'criacoes';
  if (ACOES_REMOCAO.includes(acao)) return 'remocoes';
  if (ACOES_ACESSO.includes(acao)) return 'acessos';
  return 'alteracoes';
}

/** Hora:minuto - a data completa fica no `title`. Na janela de 1h o dia e hoje. */
function formatHora(value: string) {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d);
}

const REFRESH_INTERVAL_MS = 60_000;

/* Quanto se BUSCA de uma vez (a janela inteira de 1h) e quanto se MOSTRA por
   pagina. Sao coisas diferentes de proposito.

   A paginacao e no CLIENTE, sobre a janela ja carregada, e nao no servidor. Isso
   parece o caminho errado e e o certo aqui: as abas (Tudo/Alterações/Criações/
   Remoções/Acessos) filtram em memoria e os numeros delas contam a janela inteira.
   Com paginacao no servidor, cada pagina traria um pedaco arbitrario e a aba
   "Criações 2" passaria a significar "2 nesta pagina" - o numero da aba discordaria
   do que aparece ao clicar, que e exatamente o que o comentario da contagem abaixo
   existe para evitar.

   O `rows` subiu de 60 para 300 junto com a paginacao: antes 60 era o teto do que
   a tela mostrava de uma vez, agora e o teto do que ela deixa FOLHEAR. Se a hora
   tiver mais que 300 eventos, o rodape avisa que ha mais - silencio ali seria a
   tela mentindo por omissao. */
const LINHAS_BUSCADAS = 300;
/* CINCO, FIXO - e nao `linhasIniciais()` como nas telas de lista.

   Este historico e um BLOCO DA HOME, dividindo a tela com os cartoes do parque, o
   movimento do dia, quem esta no sistema e os aplicativos mais instalados. Nas
   telas de lista o grid E a tela, e faz sentido ele crescer com a janela; aqui,
   crescer empurra os outros blocos para fora da primeira dobra.

   Cinco tambem casa com o que a Home responde: "o que acabou de acontecer?". Quem
   quer percorrer o historico tem a tela propria, e a paginacao daqui continua
   levando ao resto da hora. */
const LINHAS_POR_PAGINA = 5;
const WINDOW_HOURS = 1;

function buildUrl() {
  const desde = new Date(Date.now() - WINDOW_HOURS * 60 * 60 * 1000).toISOString();
  return `historicos/paginado?page=0&rows=${LINHAS_BUSCADAS}&ordenar=dataExecucao&direcao=desc&desde=${encodeURIComponent(
    desde
  )}`;
}

export default function HistoricoGeral() {
  const [items, setItems] = useState<HistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [modal, setModal] = useState<ModalState>({ open: false });
  const [aba, setAba] = useState<AbaId>('tudo');
  const [pagina, setPagina] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const clockRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  /* A atualizacao automatica precisa ler a pagina ATUAL, e o intervalo e criado
     uma vez - dentro dele, `pagina` ficaria congelada no valor da montagem. Por
     isso a pagina tambem vive numa ref. */
  const paginaRef = useRef(0);
  paginaRef.current = pagina;

  const fetchHistorico = async () => {
    /* NAO recarrega fora da primeira pagina.

       O historico e um feed em ordem decrescente e chegam eventos novos a cada
       minuto. Recarregar enquanto alguem le a pagina 3 empurra tudo para baixo: as
       linhas que estavam na frente dos olhos passam a ser outras, sem nada ter
       sido clicado. Na primeira pagina isso e o comportamento desejado (ver o que
       acabou de acontecer); nas seguintes e perder o lugar da leitura.

       O rodape diz que a atualizacao esta pausada, senao pareceria travado. */
    if (paginaRef.current > 0) return;

    try {
      const res = await axiosInstance.get<PaginadoResponse>(buildUrl());
      setItems(res.data?.content || []);
      setTotal(res.data?.totalElements || 0);
      setLastRefresh(new Date());
      setSecondsAgo(0);
    } catch {
      // mantém dados anteriores
    }
  };

  useEffect(() => {
    fetchHistorico();
    intervalRef.current = setInterval(fetchHistorico, REFRESH_INTERVAL_MS);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(clockRef.current);
    };
  }, []);

  useEffect(() => {
    clearInterval(clockRef.current);
    if (!lastRefresh) return;
    clockRef.current = setInterval(() => {
      setSecondsAgo(Math.round((Date.now() - lastRefresh.getTime()) / 1000));
    }, 5000);
    return () => clearInterval(clockRef.current);
  }, [lastRefresh]);

  const refreshLabel = !lastRefresh ? 'Carregando...' : secondsAgo < 10 ? 'Agora mesmo' : `Há ${secondsAgo}s`;

  const openModal = (item: HistoricoItem) => {
    const tipoInfo = TIPO_LABELS[item.tipo];
    const acaoLabel = ACAO_LABELS[item.acao] ?? `Ação ${item.acao}`;
    setModal({
      open: true,
      chaveInfo: item.chaveInfo,
      dataCampo: item.dataCampo,
      titulo: `${tipoInfo?.label ?? 'Evento'} — ${acaoLabel}`,
    });
  };

  /* Contagem por aba sobre o que ja esta em memoria - e o mesmo conjunto que a
     lista mostra, entao o numero da aba nunca discorda do que se ve ao clicar. */
  const contagem = useMemo(() => {
    const c: Record<string, number> = { tudo: items.length, alteracoes: 0, criacoes: 0, remocoes: 0, acessos: 0 };
    items.forEach((i) => {
      c[grupoDaAcao(i.acao)] += 1;
    });
    return c;
  }, [items]);

  const daAba = useMemo(
    () => (aba === 'tudo' ? items : items.filter((i) => grupoDaAcao(i.acao) === aba)),
    [items, aba]
  );

  const totalPaginas = Math.max(1, Math.ceil(daAba.length / LINHAS_POR_PAGINA));

  /* Pagina fora do alcance volta para a ultima existente. Acontece de dois jeitos:
     trocando para uma aba com menos eventos, e quando a atualizacao traz uma janela
     menor. Sem isto a tela ficaria em branco com o rodape dizendo "3 / 1". */
  const paginaValida = Math.min(pagina, totalPaginas - 1);
  useEffect(() => {
    if (pagina !== paginaValida) setPagina(paginaValida);
  }, [pagina, paginaValida]);

  const inicio = paginaValida * LINHAS_POR_PAGINA;
  const visiveis = useMemo(
    () => daAba.slice(inicio, inicio + LINHAS_POR_PAGINA),
    [daAba, inicio]
  );

  const dadosAlterados = useMemo(() => parseJsonSafe(modal.dataCampo), [modal.dataCampo]);
  const ehListaDeCampos =
    Array.isArray(dadosAlterados) && dadosAlterados.every((x) => x && typeof x === 'object' && 'campo' in x);

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-card md:p-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[14px] font-extrabold tracking-[-0.02em]">Histórico geral</h2>
          <span
            className="inline-flex h-[22px] items-center rounded-md bg-warn-soft px-2 text-[11px] font-bold text-warn"
            title={`${total.toLocaleString('pt-BR')} eventos na última hora`}
          >
            {formatCompactNumber(total)} na última hora
          </span>
        </div>

        <span className="text-[11px] text-faint">↻ {refreshLabel}</span>
      </header>

      {/* ABAS - traco embaixo, o mesmo padrao dos modais e do Manual. */}
      <div role="tablist" aria-label="Histórico geral" className="mt-3 flex gap-1 overflow-x-auto border-b border-line">
        {ABAS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={aba === a.id}
            onClick={() => {
              setAba(a.id);
              // Trocar de aba volta para a primeira pagina: continuar na pagina 3
              // de outra categoria mostra o meio de uma lista que a pessoa nunca viu.
              setPagina(0);
            }}
            className={
              aba === a.id
                ? '-mb-px flex flex-none items-center gap-1.5 border-b-2 border-primary px-3 py-2 text-[12px] font-bold text-primary'
                : '-mb-px flex flex-none items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:border-line hover:text-ink'
            }
          >
            {a.rotulo}
            <Num className="text-[10px] text-faint">{contagem[a.id] ?? 0}</Num>
          </button>
        ))}
      </div>

      {/* LISTA EM LINHAS, e nao em cartoes.

          Os cartoes (dois/tres por linha, cada um com selo de tipo, selo de acao,
          mensagem, nome, chave, hora, IP e botao) gastavam ~120px de altura para
          UM evento, e ao lado um do outro a leitura ia em zigue-zague. Um feed
          responde "o que aconteceu, em que ordem" - e ordem se le de cima para
          baixo, uma linha por evento.

          A cor do TIPO ficou num tracinho de 3px na esquerda: continua
          distinguindo as sete categorias sem sete selos coloridos disputando
          atencao com o texto do evento.

          `hidden md:table-cell` no IP: e dado de auditoria, util quando se procura,
          e o primeiro a sair quando a tela aperta. */}
      {!visiveis.length ? (
        <p className="mt-4 text-[12px] text-muted-foreground">
          {items.length
            ? 'Nenhum evento desta categoria na última hora.'
            : 'Nenhum evento registrado na última hora.'}
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-line text-[10px] font-bold uppercase tracking-[0.07em] text-faint">
                <th className="py-1.5 pr-3 font-bold">Hora</th>
                <th className="py-1.5 pr-3 font-bold">Ação</th>
                <th className="py-1.5 pr-3 font-bold">Evento e origem</th>
                <th className="hidden py-1.5 pr-3 font-bold md:table-cell">IP</th>
                <th className="py-1.5 font-bold" />
              </tr>
            </thead>
            <tbody>
              {visiveis.map((item) => {
                const tipoInfo = TIPO_LABELS[item.tipo] ?? { label: `Tipo ${item.tipo}`, color: '#6b7280' };
                const acaoLabel = ACAO_LABELS[item.acao] ?? `Ação ${item.acao}`;
                const nomeUsuario = item.usuarioExecutante?.nome || item.usuarioExecutante?.email || null;
                const chaveNome = item.chaveInfo?.nome || item.chaveInfo?.name || null;
                const temDetalhe = Boolean(item.chaveInfo || item.dataCampo);
                const chaveCrua =
                  item.chave && item.chave !== 'undefined' && item.chave !== 'null' ? item.chave : null;

                return (
                  <tr key={item.codigo} className="border-b border-line last:border-0 align-top hover:bg-surface-2">
                    <td className="whitespace-nowrap py-2 pr-3">
                      <Num className="text-[12px] text-muted-foreground" title={formatDateTime(item.dataExecucao)}>
                        {formatHora(item.dataExecucao)}
                      </Num>
                    </td>

                    <td className="whitespace-nowrap py-2 pr-3">
                      <span
                        className="inline-flex h-[19px] items-center rounded px-1.5 text-[10px] font-bold uppercase"
                        style={{ backgroundColor: `${tipoInfo.color}1f`, color: tipoInfo.color }}
                        title={tipoInfo.label}
                      >
                        {acaoLabel}
                      </span>
                    </td>

                    <td className="py-2 pr-3">
                      <span className="flex items-start gap-2">
                        {/* Tracinho da categoria - a cor sem mais um selo. */}
                        <span
                          aria-hidden
                          className="mt-1 h-3.5 w-[3px] flex-none rounded"
                          style={{ backgroundColor: tipoInfo.color }}
                          title={tipoInfo.label}
                        />
                        <span className="min-w-0">
                          <span className="block text-[12px] font-semibold leading-snug text-ink">
                            {fixEncoding(item.mensagem) || '—'}
                          </span>
                          {(nomeUsuario || chaveNome || chaveCrua) && (
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {nomeUsuario}
                              {nomeUsuario && (chaveNome || chaveCrua) ? ' · ' : ''}
                              {chaveNome || (chaveCrua ? <Num>{chaveCrua}</Num> : null)}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>

                    <td className="hidden whitespace-nowrap py-2 pr-3 md:table-cell">
                      {item.ip ? <Num className="text-[11px] text-faint">{item.ip}</Num> : null}
                    </td>

                    <td className="py-2 text-right">
                      {/* Sem botao quando nao ha o que abrir: antes ele existia
                          sempre e abria um modal dizendo "nenhum dado adicional". */}
                      {temDetalhe && (
                        <button
                          type="button"
                          title="Ver detalhes"
                          onClick={() => openModal(item)}
                          className="rounded border border-line px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground hover:text-ink"
                        >
                          detalhes
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* RODAPE DE PAGINACAO.

          Aparece com mais de uma pagina - com uma so, o rodape seria enfeite. Diz
          o intervalo em vez de so o numero da pagina ("26-50 de 43" e verificavel;
          "2 / 2" nao diz nada sobre o tamanho). */}
      {daAba.length > LINHAS_POR_PAGINA && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line pb-1 pr-16 pt-3">
          <span className="text-[11px] text-muted-foreground">
            Mostrando{' '}
            <Num className="font-semibold text-ink">
              {inicio + 1}–{Math.min(inicio + LINHAS_POR_PAGINA, daAba.length)}
            </Num>{' '}
            de <Num className="font-semibold text-ink">{daAba.length}</Num>
            {/* A janela pode ter mais do que se buscou. Sem este aviso a tela
                pareceria estar mostrando tudo. */}
            {total > items.length ? (
              <span className="text-faint">
                {' '}
                · a última hora tem <Num>{total.toLocaleString('pt-BR')}</Num> eventos; aqui estão os{' '}
                <Num>{items.length}</Num> mais recentes
              </span>
            ) : null}
            {paginaValida > 0 && <span className="text-warn"> · atualização pausada nesta página</span>}
          </span>

          <span className="flex items-center gap-2">
            <button
              type="button"
              disabled={paginaValida === 0}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              className="h-7 rounded-md border border-line px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
            >
              Anterior
            </button>
            <Num className="text-[11px] text-muted-foreground">
              {paginaValida + 1} / {totalPaginas}
            </Num>
            <button
              type="button"
              disabled={paginaValida >= totalPaginas - 1}
              onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
              className="h-7 rounded-md border border-line px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
            >
              Próxima
            </button>
          </span>
        </div>
      )}

      <Modal
        aberto={modal.open}
        titulo={modal.titulo || 'Detalhes do evento'}
        largura="max-w-[620px]"
        onFechar={() => setModal({ open: false })}
        rodape={<BotaoSecundario onClick={() => setModal({ open: false })}>Fechar</BotaoSecundario>}
      >
        {modal.chaveInfo && (
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.07em] text-faint">
              Entidade vinculada ({modal.chaveInfo.tipo})
            </div>
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full border-collapse text-[12px]">
                <tbody>
                  {chaveInfoToRows(modal.chaveInfo).map((row) => (
                    <tr key={row.label} className="border-b border-line last:border-0">
                      <td className="w-[35%] px-3 py-1.5 font-semibold text-muted-foreground">{row.label}</td>
                      <td className="break-all px-3 py-1.5">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {modal.dataCampo && (
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.07em] text-faint">Dados alterados</div>

            {ehListaDeCampos ? (
              // Formato real do `dataCampo` do sistema: array de {campo, antigo, novo}.
              <div className="overflow-hidden rounded-lg border border-line">
                <table className="w-full border-collapse text-[12px]">
                  <thead className="bg-soft">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-bold">Campo</th>
                      <th className="w-[30%] px-3 py-1.5 text-left font-bold">Antes</th>
                      <th className="w-[30%] px-3 py-1.5 text-left font-bold">Depois</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosAlterados.map((linha: any, i: number) => (
                      <tr key={`${linha.campo}-${i}`} className="border-t border-line align-top">
                        <td className="px-3 py-1.5 font-semibold">{linha.campo}</td>
                        <td className="px-3 py-1.5 text-muted-foreground line-through decoration-danger">
                          {linha.antigo || '—'}
                        </td>
                        <td className="px-3 py-1.5 font-semibold">{linha.novo || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : dadosAlterados ? (
              <div className="overflow-hidden rounded-lg border border-line">
                <table className="w-full border-collapse text-[12px]">
                  <tbody>
                    {Object.entries(dadosAlterados).map(([key, val]) => (
                      <tr key={key} className="border-b border-line last:border-0">
                        <td className="w-[35%] px-3 py-1.5 font-semibold text-muted-foreground">{key}</td>
                        <td className="break-all px-3 py-1.5">
                          {val === null ? '—' : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-soft p-3 text-[11px]">
                {modal.dataCampo}
              </pre>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
}
